import { Button, Checkbox, Modal, Pagination, Table, Tag, Tooltip, Alert, Spin } from 'antd';
import ExcelJS from 'exceljs';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';

import { api, showErrorNotification } from '@med-fe/util';
import { fetchHolidayPartialLocations, fetchHolidaysFull } from '../HolidaySchedule/saga';
import styles from './FutureScheduleGrid.module.scss';

const WEEK_COUNT = 4;
const DAYS_PER_WEEK = 7;
const TOTAL_DAYS = WEEK_COUNT * DAYS_PER_WEEK;
const DETAIL_TABLE_WIDTH = 1186;
const WEEK_DAYS = [
  { name: 'Monday', label: 'M' },
  { name: 'Tuesday', label: 'T' },
  { name: 'Wednesday', label: 'W' },
  { name: 'Thursday', label: 'T' },
  { name: 'Friday', label: 'F' },
  { name: 'Saturday', label: 'S' },
  { name: 'Sunday', label: 'S' },
];
const EXPORT_WEEK_DAYS = WEEK_DAYS;

type ScheduleEntry = {
  dayOfWeek?: string;
  open?: string;
  close?: string;
  validFrom?: string;
  validTo?: string;
};

type Holiday = {
  date: string;
  name: string;
  isFull: boolean;
  locationIds: Set<string>;
  locationNumbers: Set<string>;
};

type DaySchedule = {
  date: string;
  dayOfWeek: string;
  status: 'open' | 'more' | 'less' | 'closed';
  hours: string;
  holidays: string[];
};

type FutureScheduleRow = {
  key: string;
  id: string;
  locationNumber: string;
  locationName: string;
  region: string;
  district: string;
  timezone: string;
  days: DaySchedule[];
  exportDays: DaySchedule[];
  weeklyDays: DaySchedule[];
  openDays: number;
};

type Week = {
  start: Moment;
  end: Moment;
};

const getDateOnly = (value: unknown) => (value ? String(value).slice(0, 10) : '');

const getHolidayName = (holiday: any) => holiday?.HolidayName || holiday?.Name || holiday?.name || '';

const getHolidayDate = (holiday: any) => getDateOnly(holiday?.HolidayDate || holiday?.Date || holiday?.date);

const isScheduleOpen = (schedule?: ScheduleEntry) => {
  const open = schedule?.open?.trim();
  const close = schedule?.close?.trim();

  return Boolean(
    open &&
      close &&
      !['00:00', '12:00 AM'].includes(open.toUpperCase()) &&
      !['00:00', '12:00 AM'].includes(close.toUpperCase())
  );
};

const getScheduleForDate = (schedules: ScheduleEntry[] | undefined, date: string) => {
  if (!schedules) {
    return undefined;
  }

  const exactDateSchedule = schedules.find((schedule) => getDateOnly(schedule.validFrom) === date);
  if (exactDateSchedule) {
    return exactDateSchedule;
  }

  const dayOfWeek = moment(date, 'YYYY-MM-DD', true).format('dddd');
  return schedules.find((schedule) => {
    const validFrom = getDateOnly(schedule.validFrom);
    const validTo = getDateOnly(schedule.validTo);
    return (
      schedule.dayOfWeek?.toLowerCase() === dayOfWeek.toLowerCase() &&
      validFrom <= date &&
      (!validTo || validTo >= date)
    );
  });
};

const getHours = (schedule?: ScheduleEntry) =>
  isScheduleOpen(schedule) ? `${schedule?.open} - ${schedule?.close}` : '';

const getScheduleDurationMinutes = (schedule?: ScheduleEntry) => {
  if (!schedule) {
    return null;
  }
  if (!isScheduleOpen(schedule)) {
    return 0;
  }

  const formats = ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm', 'HH:mm:ss'];
  const open = moment(schedule.open, formats, true);
  const close = moment(schedule.close, formats, true);
  if (!open.isValid() || !close.isValid()) {
    return null;
  }

  const duration = close.diff(open, 'minutes');
  return duration < 0 ? duration + 24 * 60 : duration;
};

const formatExcelTime = (value: string) => {
  const parsed = moment(value, ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm', 'HH:mm:ss'], true);
  return parsed.isValid() ? parsed.format(parsed.minutes() === 0 ? 'hA' : 'h:mmA') : value.replace(/\s+/g, '');
};

const formatExcelHours = (day?: DaySchedule) => {
  if (!day || day.status === 'closed' || !day.hours) {
    return 'CLOSED';
  }

  const [open, close] = day.hours.split(/\s*-\s*/);
  const hours = `${formatExcelTime(open)}-${formatExcelTime(close)}`;
  return day.status === 'more'
    ? `${hours} (More than Core)`
    : day.status === 'less'
    ? `${hours} (Less than Core)`
    : hours;
};

const mapFutureLocation = (location: any, holidayMap: Map<string, Holiday[]>, weeks: Week[]): FutureScheduleRow => {
  const locationId = String(location.id ?? location.locationNumber ?? '');
  const locationNumber = location.locationNumber || '';
  const mapDate = (date: string, dayName: string) => {
    const applicableHolidays = (holidayMap.get(date) || []).filter(
      (holiday) => holiday.isFull || holiday.locationIds.has(locationId) || holiday.locationNumbers.has(locationNumber)
    );
    const currentSchedule = getScheduleForDate(location.schedules, date);
    const coreSchedule = getScheduleForDate(location.coreSchedules, date);
    const currentIsOpen = isScheduleOpen(currentSchedule);
    const hasFullHoliday = applicableHolidays.some((holiday) => holiday.isFull);
    const isOpen = currentIsOpen && !hasFullHoliday;
    const currentDuration = getScheduleDurationMinutes(currentSchedule);
    const coreDuration = getScheduleDurationMinutes(coreSchedule);
    const status = !isOpen
      ? 'closed'
      : currentDuration != null && coreDuration != null && currentDuration > coreDuration
      ? 'more'
      : currentDuration != null && coreDuration != null && currentDuration < coreDuration
      ? 'less'
      : 'open';

    return {
      date,
      dayOfWeek: dayName,
      status,
      hours: isOpen ? getHours(currentSchedule) : '',
      holidays: applicableHolidays.map((holiday) => holiday.name),
    } as DaySchedule;
  };
  const days = weeks.flatMap((week) =>
    WEEK_DAYS.map((day, dayIndex) => mapDate(week.start.clone().add(dayIndex, 'days').format('YYYY-MM-DD'), day.name))
  );
  const exportDays = weeks.flatMap((week) =>
    EXPORT_WEEK_DAYS.map((day, dayIndex) =>
      mapDate(week.start.clone().add(dayIndex, 'days').format('YYYY-MM-DD'), day.name)
    )
  );
  const firstSunday = weeks[0].start.clone().day(0);
  const weeklyDays = Array.from({ length: TOTAL_DAYS }, (_, dayIndex) => {
    const date = firstSunday.clone().add(dayIndex, 'days');
    return mapDate(date.format('YYYY-MM-DD'), date.format('dddd'));
  });

  return {
    key: locationId,
    id: locationId,
    locationNumber,
    locationName: location.locationName || '',
    region: location.region || location.regionName || '',
    district: location.district || location.districtName || '',
    timezone: location.timezone || '',
    days,
    exportDays,
    weeklyDays,
    openDays: days.filter((day) => day.status !== 'closed').length,
  };
};

const downloadRowsAsExcel = async (rows: FutureScheduleRow[], weeks: Week[]) => {
  const headers = [
    'Location Number',
    'Location Name',
    'Region',
    'District',
    'Week Start',
    'Timezone',
    ...weeks.flatMap(() => ['Date', 'Hours']),
  ];
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Upcoming');
  worksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 6 }];
  worksheet.columns = headers.map((header, index) => ({
    header,
    width:
      index === 0
        ? 14
        : index === 1
        ? 20
        : index === 2 || index === 3
        ? 13
        : index === 4
        ? 14
        : index === 5
        ? 10
        : index % 2 === 0
        ? 17
        : 16,
  }));

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle' };

  rows.forEach((row) => {
    Array.from({ length: DAYS_PER_WEEK }, (_, dayIndex) => {
      const values = [
        dayIndex === 0 ? row.locationNumber : '',
        dayIndex === 0 ? row.locationName : '',
        dayIndex === 0 ? row.region : '',
        dayIndex === 0 ? row.district : '',
        dayIndex === 0 ? weeks[0].start.toDate() : '',
        dayIndex === 0 ? row.timezone : '',
        ...weeks.flatMap((_week, weekIndex) => {
          const day = row.exportDays[weekIndex * DAYS_PER_WEEK + dayIndex];
          return [day ? moment(day.date).toDate() : '', formatExcelHours(day)];
        }),
      ];
      const excelRow = worksheet.addRow(values);
      excelRow.height = 20;
      if (dayIndex === 0) {
        excelRow.getCell(5).numFmt = 'm/d/yyyy';
        excelRow.getCell(5).alignment = { horizontal: 'left' };
      }

      weeks.forEach((_week, weekIndex) => {
        const dateCell = excelRow.getCell(7 + weekIndex * 2);
        dateCell.numFmt = 'ddd m/d/yyyy';
        dateCell.font = { bold: true };
        dateCell.alignment = { horizontal: 'left' };

        const day = row.exportDays[weekIndex * DAYS_PER_WEEK + dayIndex];
        if (day?.status === 'more' || day?.status === 'less') {
          const hoursCell = excelRow.getCell(8 + weekIndex * 2);
          hoursCell.font = { bold: true, color: { argb: 'FFFF0000' } };
        }
      });
      return excelRow;
    });
  });

  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };

  const weeklyWorksheet = workbook.addWorksheet('Weekly View');
  const firstSunday = weeks[0].start.clone().day(0);
  const weeklyHeaders = [
    'Location Number',
    'Location Name',
    'Region',
    'District',
    'Week Start',
    'Timezone',
    ...Array.from(
      { length: DAYS_PER_WEEK },
      (_, dayIndex) =>
        `${firstSunday.clone().add(dayIndex, 'days').format('ddd')} ${firstSunday
          .clone()
          .add(dayIndex, 'days')
          .format('M/D/YYYY')}`
    ),
  ];
  weeklyWorksheet.columns = weeklyHeaders.map((header, index) => ({
    header,
    width:
      index === 0 ? 18 : index === 1 ? 24 : index === 2 || index === 3 ? 18 : index === 4 ? 16 : index === 5 ? 12 : 18,
  }));
  weeklyWorksheet.views = [{ state: 'frozen', ySplit: 1, xSplit: 6 }];

  const styleWeeklyDateHeader = (headerRow: ExcelJS.Row) => {
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle' };
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
    });
  };

  const initialWeeklyHeader = weeklyWorksheet.getRow(1);
  styleWeeklyDateHeader(initialWeeklyHeader);

  rows.forEach((row, rowIndex) => {
    for (let weekIndex = 0; weekIndex < WEEK_COUNT; weekIndex += 1) {
      const weekStart = firstSunday.clone().add(weekIndex, 'weeks');
      if (rowIndex > 0 || weekIndex > 0) {
        const dateHeader = weeklyWorksheet.addRow([
          '',
          '',
          '',
          '',
          '',
          '',
          ...Array.from(
            { length: DAYS_PER_WEEK },
            (_, dayIndex) =>
              `${weekStart.clone().add(dayIndex, 'days').format('ddd')} ${weekStart
                .clone()
                .add(dayIndex, 'days')
                .format('M/D/YYYY')}`
          ),
        ]);
        styleWeeklyDateHeader(dateHeader);
      }

      const weekDays = row.weeklyDays.slice(weekIndex * DAYS_PER_WEEK, (weekIndex + 1) * DAYS_PER_WEEK);
      const weeklyDataRow = weeklyWorksheet.addRow([
        row.locationNumber,
        row.locationName,
        row.region,
        row.district,
        weekStart.toDate(),
        row.timezone,
        ...weekDays.map((day) => {
          const value = formatExcelHours(day);
          return value.split(' (')[0];
        }),
      ]);
      weeklyDataRow.height = 20;
      weeklyDataRow.getCell(5).numFmt = 'm/d/yyyy';
      weeklyDataRow.getCell(5).alignment = { horizontal: 'left' };

      weekDays.forEach((day, dayIndex) => {
        if (day.status === 'more' || day.status === 'less') {
          weeklyDataRow.getCell(7 + dayIndex).font = { color: { argb: 'FFFF0000' } };
        }
      });
    }

    if (rowIndex < rows.length - 1) {
      weeklyWorksheet.addRow([]);
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = rows.length === 1 ? `${rows[0].locationNumber}-upcoming-schedule.xlsx` : 'upcoming-schedules.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const exportRowsAsExcel = (rows: FutureScheduleRow[], weeks: Week[]) =>
  downloadRowsAsExcel(rows, weeks).catch((reason) => {
    showErrorNotification('Could not export the Upcoming schedule to Excel.', reason);
  });

function FutureScheduleGrid({ search, setSearch }) {
  const weeks = useMemo(() => {
    const firstMonday = moment().startOf('isoWeek').add(1, 'week');
    return Array.from({ length: WEEK_COUNT }, (_, index) => {
      const start = firstMonday.clone().add(index, 'weeks');
      return { start, end: start.clone().add(6, 'days') };
    });
  }, []);
  const exportWeeks = weeks;
  const [rows, setRows] = useState<FutureScheduleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedRows, setSelectedRows] = useState<Map<string, FutureScheduleRow>>(new Map());
  const [selectedLocation, setSelectedLocation] = useState<FutureScheduleRow | null>(null);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const selectionRequestRef = useRef(0);
  const searchScope = JSON.stringify({
    search: search.search,
    statuses: search.statuses,
    regionIds: search.regionIds,
    districtIds: search.districtIds,
    locationIds: search.locationIds,
  });

  useEffect(() => {
    selectionRequestRef.current += 1;
    setSelectionLoading(false);
    setSelectedRows(new Map());
    setSelectedLocation(null);
  }, [searchScope]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const years = Array.from(
      new Set(
        exportWeeks
          .map((week) => week.start.year())
          .concat(exportWeeks[exportWeeks.length - 1].end.year(), exportWeeks[0].start.clone().day(0).year())
      )
    );

    const scheduleRequest = api.post('/locations/search', {
      ...search,
      activeTab: 'schedule',
      scheduleSubtab: 'future',
      effectiveDate: exportWeeks[0].start.clone().day(0).format('YYYY-MM-DD'),
    });

    Promise.all([
      scheduleRequest,
      fetchHolidaysFull(),
      Promise.all(years.map((year) => fetchHolidayPartialLocations(year))),
    ])
      .then(([scheduleResult, fullHolidayResults, partialHolidayResults]) => {
        if (cancelled) {
          return;
        }

        if (!Array.isArray(scheduleResult?.contents)) {
          throw new Error('The schedule search returned an unexpected response.');
        }

        const holidayMap = new Map<string, Holiday[]>();
        const addHoliday = (
          date: string,
          name: string,
          isFull: boolean,
          locationIds: string[] = [],
          locationNumbers: string[] = []
        ) => {
          if (!date || !name) {
            return;
          }

          const existing = holidayMap.get(date) || [];
          const holiday = existing.find((item) => item.name === name && item.isFull === isFull);
          if (holiday) {
            locationIds.forEach((id) => holiday.locationIds.add(id));
            locationNumbers.forEach((number) => holiday.locationNumbers.add(number));
            return;
          }

          existing.push({
            date,
            name,
            isFull,
            locationIds: new Set(locationIds),
            locationNumbers: new Set(locationNumbers),
          });
          holidayMap.set(date, existing);
        };

        (Array.isArray(fullHolidayResults) ? fullHolidayResults : []).forEach((holiday: any) => {
          addHoliday(getHolidayDate(holiday), getHolidayName(holiday), true);
        });

        partialHolidayResults.flat().forEach((holiday: any) => {
          const locationIds = (holiday?.Locations || holiday?.locations || [])
            .map((location: any) =>
              location?.locationId != null || location?.LocationId != null
                ? String(location.locationId ?? location.LocationId)
                : ''
            )
            .filter(Boolean);
          const locationNumbers = (holiday?.Locations || holiday?.locations || [])
            .map((location: any) => location?.locationNumber || location?.LocationNumber || '')
            .filter(Boolean);

          addHoliday(getHolidayDate(holiday), getHolidayName(holiday), false, locationIds, locationNumbers);
        });

        const mappedRows = scheduleResult.contents.map((location) => mapFutureLocation(location, holidayMap, weeks));

        const allHolidays = Array.from(holidayMap.values())
          .flat()
          .filter(
            (holiday) =>
              holiday.date >= exportWeeks[0].start.format('YYYY-MM-DD') &&
              holiday.date <= exportWeeks[exportWeeks.length - 1].end.format('YYYY-MM-DD')
          )
          .sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));

        setRows(mappedRows);
        setTotal(scheduleResult.total || 0);
        setHolidays(allHolidays);
      })
      .catch((reason: unknown) => {
        if (cancelled) {
          return;
        }

        const loadError = reason instanceof Error ? reason : new Error('Could not load future schedules.');
        setError(loadError);
        setRows([]);
        setTotal(0);
        showErrorNotification('Could not load future schedules.', reason);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [search, weeks, exportWeeks]);

  const isAllSelected = total > 0 && selectedRows.size >= total;
  const isPartiallySelected = selectedRows.size > 0 && !isAllSelected;
  const rangeLabel = `${weeks[0].start.format('MMM D')} – ${weeks[weeks.length - 1].end.format('MMM D')}`;

  const selectAllLocations = async (checked: boolean) => {
    const requestId = ++selectionRequestRef.current;
    if (!checked) {
      setSelectionLoading(false);
      setSelectedRows(new Map());
      return;
    }

    setSelectionLoading(true);
    try {
      const result = await api.post('/locations/search', {
        ...search,
        page: 0,
        size: total,
        activeTab: 'schedule',
        scheduleSubtab: 'future',
        effectiveDate: exportWeeks[0].start.clone().day(0).format('YYYY-MM-DD'),
      });
      if (!Array.isArray(result?.contents)) {
        throw new Error('The schedule search returned an unexpected response.');
      }
      if (typeof result.total === 'number' && result.contents.length < result.total) {
        throw new Error('Could not load every matching location. Try selecting a smaller result set.');
      }

      const holidayMap = new Map<string, Holiday[]>();
      holidays.forEach((holiday) => {
        holidayMap.set(holiday.date, [...(holidayMap.get(holiday.date) || []), holiday]);
      });
      const allRows = result.contents.map((location) => mapFutureLocation(location, holidayMap, weeks));
      if (requestId === selectionRequestRef.current) {
        setSelectedRows(new Map(allRows.map((row) => [row.key, row])));
      }
    } catch (reason) {
      if (requestId === selectionRequestRef.current) {
        showErrorNotification('Could not select all locations.', reason);
      }
    } finally {
      if (requestId === selectionRequestRef.current) {
        setSelectionLoading(false);
      }
    }
  };

  const selectRow = (row: FutureScheduleRow, checked: boolean) => {
    setSelectedRows((previous) => {
      const updated = new Map(previous);
      if (checked) {
        updated.set(row.key, row);
      } else {
        updated.delete(row.key);
      }
      return updated;
    });
  };

  const openWeek = (row: FutureScheduleRow, weekIndex: number) => {
    setSelectedLocation(row);
    setSelectedWeekIndex(weekIndex);
  };

  const columns: any[] = [
    {
      title: (
        <Checkbox
          aria-label='Select all locations'
          checked={isAllSelected}
          indeterminate={isPartiallySelected}
          disabled={loading || selectionLoading || total === 0}
          onChange={(event) => selectAllLocations(event.target.checked)}
        />
      ),
      key: 'select',
      width: 48,
      fixed: 'left',
      render: (_: unknown, row: FutureScheduleRow) => (
        <Checkbox
          aria-label={`Select ${row.locationNumber}`}
          checked={selectedRows.has(row.key)}
          disabled={selectionLoading}
          onChange={(event) => selectRow(row, event.target.checked)}
        />
      ),
    },
    {
      title: 'Location Number',
      dataIndex: 'locationNumber',
      key: 'locationNumber',
      width: 140,
      fixed: 'left',
      render: (value: string) => value,
    },
    {
      title: 'Location name',
      dataIndex: 'locationName',
      key: 'locationName',
      width: 260,
      fixed: 'left',
    },
    {
      title: 'Status',
      key: 'status',
      width: 105,
      render: (_: unknown, row: FutureScheduleRow) => (
        <span className={`${styles.locationStatus} ${row.openDays > 0 ? styles.open : styles.closed}`}>
          {row.openDays > 0 ? 'OPEN' : 'CLOSED'}
        </span>
      ),
    },
    {
      title: 'Open days',
      key: 'openDays',
      width: 112,
      align: 'center',
      render: (_: unknown, row: FutureScheduleRow) => `${row.openDays}/${TOTAL_DAYS}`,
    },
    {
      title: 'Export',
      key: 'export',
      width: 88,
      align: 'center',
      render: (_: unknown, row: FutureScheduleRow) => (
        <Button size='small' onClick={() => void exportRowsAsExcel([row], exportWeeks)}>
          Excel
        </Button>
      ),
    },
    ...weeks.map((week, weekIndex) => ({
      title: (
        <div className={styles.weekHeading}>
          <span>
            WK {weekIndex + 1} {week.start.format('MMM D')}
          </span>
          <div className={styles.weekdayLabels}>
            {WEEK_DAYS.map((day, dayIndex) => (
              <span key={`${day.name}-${dayIndex}`}>{day.label}</span>
            ))}
          </div>
        </div>
      ),
      key: `week-${weekIndex}`,
      width: 184,
      align: 'center',
      render: (_: unknown, row: FutureScheduleRow) => (
        <div className={styles.weekDays}>
          {row.days.slice(weekIndex * 7, weekIndex * 7 + 7).map((day) => {
            const tooltip = [
              `${moment(day.date).format('dddd, MMM D')}: ${day.status.toUpperCase()}`,
              day.hours,
              ...day.holidays,
            ]
              .filter(Boolean)
              .join(' · ');

            return (
              <Tooltip title={tooltip} key={day.date}>
                <button
                  type='button'
                  aria-label={`${moment(day.date).format('dddd, MMMM D')}: ${day.status}`}
                  className={`${styles.dayMarker} ${styles[day.status]}`}
                  onClick={() => openWeek(row, weekIndex)}
                />
              </Tooltip>
            );
          })}
        </div>
      ),
    })),
  ];

  const selectedWeek = weeks[selectedWeekIndex];
  const selectedWeekDays = selectedLocation?.days.slice(selectedWeekIndex * 7, selectedWeekIndex * 7 + 7) || [];
  const selectedWeekOpenDays = selectedWeekDays.filter((day) => day.status !== 'closed').length;
  const weekHolidaySummary =
    Array.from(new Set(selectedWeekDays.flatMap((day) => day.holidays))).join(', ') || 'No holidays this week';
  const dialogColumns: any[] = [
    {
      title: 'Location',
      key: 'location',
      width: 210,
      fixed: 'left',
      render: () => (
        <span>
          <strong className={styles.dialogLocationNumber}>{selectedLocation?.locationNumber}</strong>{' '}
          {selectedLocation?.locationName}
        </span>
      ),
    },
    ...selectedWeekDays.map((day) => ({
      title: (
        <span className={styles.dialogDayHeading}>
          <strong>{moment(day.date).format('ddd').toUpperCase()}</strong> {moment(day.date).format('MMM D')}
        </span>
      ),
      key: day.date,
      width: 128,
      align: 'center',
      render: () => (
        <Tooltip title={[day.status.toUpperCase(), day.hours, ...day.holidays].filter(Boolean).join(' · ')}>
          <span className={`${styles.dialogScheduleCell} ${styles[day.status]}`}>{day.hours || 'Closed'}</span>
        </Tooltip>
      ),
    })),
    {
      title: 'Open days',
      key: 'openDays',
      width: 80,
      align: 'center',
      render: () => `${selectedWeekOpenDays}/7`,
    },
  ];

  return (
    <div className={styles.futureSchedule}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <strong>Next 4 weeks: {rangeLabel}</strong>
          <div className={styles.legend} aria-label='Schedule status legend'>
            <span>
              <i className={styles.open} />
              Open
            </span>
            <span>
              <i className={styles.more} />
              More than Core
            </span>
            <span>
              <i className={styles.less} />
              Less than Core
            </span>
            <span>
              <i className={styles.closed} />
              Closed
            </span>
          </div>
          <div className={styles.holidayList}>
            {holidays.map((holiday) => (
              <Tag key={`${holiday.date}-${holiday.name}`}>
                {moment(holiday.date).format('MMM D')} · {holiday.name}
              </Tag>
            ))}
          </div>
        </div>
        <div className={styles.exportControls}>
          <span className={styles.selectionStatus} role='status' aria-live='polite'>
            {selectionLoading && <Spin size='small' />}
            {selectionLoading ? 'Selecting locations...' : `${selectedRows.size} selected`}
          </span>
          <Button
            size='small'
            disabled={selectedRows.size === 0 || selectionLoading}
            onClick={() => void exportRowsAsExcel(Array.from(selectedRows.values()), exportWeeks)}
          >
            Export selected
          </Button>
        </div>
      </div>

      {error && (
        <Alert type='error' showIcon message='Future schedules could not be loaded.' description={error.message} />
      )}

      <Table
        className={styles.scheduleTable}
        rowKey='key'
        dataSource={rows}
        columns={columns}
        pagination={false}
        loading={loading}
        scroll={{ x: 753 + WEEK_COUNT * 184, y: 'calc(100vh - 19em)' }}
      />

      <div className={styles.pagination}>
        <Pagination
          current={(search.page || 0) + 1}
          pageSize={search.size}
          pageSizeOptions={['15', '25', '50', '100']}
          showSizeChanger
          total={total}
          onChange={(page, pageSize) => setSearch({ page: pageSize !== search.size ? 0 : page - 1, size: pageSize })}
        />
      </div>

      <Modal
        visible={!!selectedLocation}
        title={
          selectedLocation && selectedWeek ? (
            <div className={styles.dialogHeader}>
              <div className={styles.dialogHeading}>
                <strong>
                  Week {selectedWeekIndex + 1} · {selectedWeek.start.format('MMM D')} –{' '}
                  {selectedWeek.end.format('MMM D')}
                </strong>
                <span>
                  {selectedLocation.locationNumber} · {selectedLocation.locationName} · {weekHolidaySummary}
                </span>
              </div>
              <div className={styles.dialogNavigation}>
                <Button
                  size='small'
                  disabled={selectedWeekIndex === 0}
                  onClick={() => setSelectedWeekIndex((index) => Math.max(0, index - 1))}
                >
                  ← Prev
                </Button>
                <Button
                  size='small'
                  disabled={selectedWeekIndex === weeks.length - 1}
                  onClick={() => setSelectedWeekIndex((index) => Math.min(weeks.length - 1, index + 1))}
                >
                  Next →
                </Button>
              </div>
            </div>
          ) : null
        }
        footer={null}
        width='90vw'
        centered
        className={styles.weekDialog}
        bodyStyle={{
          padding: '0 0 12px',
          maxHeight: 'calc(100vh - 170px)',
          overflowY: 'auto',
        }}
        onCancel={() => setSelectedLocation(null)}
      >
        <Table
          className={styles.dialogTable}
          rowKey='key'
          dataSource={selectedLocation ? [selectedLocation] : []}
          columns={dialogColumns}
          pagination={false}
          scroll={{ x: DETAIL_TABLE_WIDTH }}
        />
      </Modal>
    </div>
  );
}

export default FutureScheduleGrid;
